import useSWR, { mutate as mutateGlobalCache } from 'swr'
import { SocialWalletUsageSummary } from '@digital-twins/shared-types'
import { useSocialListeningApi } from './SocialListeningApiContext'
import { useAuth } from '@/lib/config/auth-provider'

export const SOCIAL_POSTS_USAGE_SWR_KEY = 'social-listening/usage'

/**
 * Refetches the posts meter wherever it is shown. Call it after anything that
 * fetches posts (a Topic created, a channel added, "Update now", an update
 * that finished).
 */
export function refreshSocialPostsUsage(): Promise<unknown> {
  return mutateGlobalCache(SOCIAL_POSTS_USAGE_SWR_KEY)
}

interface UseSocialPostsUsageResult {
  usage: SocialWalletUsageSummary | null
  isLoading: boolean
  hasError: boolean
  refresh: () => Promise<SocialWalletUsageSummary | undefined>
}

/**
 * The org's social wallet this month (credits spent and held, posts added),
 * for the Social tab meter and the Topic dialog. With the org flag off no
 * request is made; a failed request hides the meter (`usage` stays null).
 */
export function useSocialPostsUsage(shouldLoad: boolean): UseSocialPostsUsageResult {
  const socialListeningApi = useSocialListeningApi()
  const { user } = useAuth()
  const isOrgFlagOn = user?.featureFlags?.socialListening === true
  const { data, error, isLoading, mutate } = useSWR<SocialWalletUsageSummary>(
    shouldLoad && isOrgFlagOn ? SOCIAL_POSTS_USAGE_SWR_KEY : null,
    () => socialListeningApi.getPostsUsage(),
    { revalidateOnFocus: false },
  )

  return {
    usage: error ? null : (data ?? null),
    isLoading: shouldLoad && isOrgFlagOn && isLoading,
    hasError: Boolean(error),
    refresh: () => mutate(),
  }
}
